import { type TypedDocumentNode } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { AiLoader, AiSlimBanner, Css, RichTextField, useSnackbar, useTestIds } from "@homebound/beam";
import { useEffect, useState } from "react";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  GenerateReadyPlanVersionNotesDocument,
  GenerateReadyPlanVersionNotesQuery,
  GenerateReadyPlanVersionNotesQueryVariables,
} from "src/routes/libraries/plan-package-v2/components/GenerateReadyPlanVersionNotes.generated";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { useBulkPublishProductOfferingsModal_SaveReadyPlanAggregateVersionsMutation } from "src/routes/libraries/product-offerings/overview/BulkPublishProductOfferingsModal.generated";
import { ProductOfferingsOverviewPage_ProductOfferingFragment } from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewPage.generated";
import { count, fail } from "src/utils";

type BulkPublishProductOfferingsModalProps = {
  /** Offerings with a publishable draft. */
  productOfferings: ProductOfferingsOverviewPage_ProductOfferingFragment[];
  /** Called when the publish request is sent. */
  onPublish: () => void;
};

/** Publishes several product offerings' drafts at once, each with its own version notes. */
export function BulkPublishProductOfferingsModal(props: BulkPublishProductOfferingsModalProps) {
  const { productOfferings, onPublish } = props;
  const label = `Publish ${count(productOfferings, "Product Offering")}`;
  const [save] = useBulkPublishProductOfferingsModal_SaveReadyPlanAggregateVersionsMutation();
  const { triggerNotice } = useSnackbar();
  const tid = useTestIds(props, "bulkPublish");
  const [versionNotes, setVersionNotes] = useState<Record<string, string | undefined>>({});
  const [generating, setGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const client = useApolloClient();

  // Draft the notes as soon as the modal opens.
  useEffect(() => {
    async function generateNotes() {
      setGenerating(true);
      // allSettled so one offering's failure doesn't discard the notes generated for the others
      const results = await Promise.allSettled(
        productOfferings.map(async (po) => {
          // `client.query` runs one query per offering in parallel, which a single query hook can't do
          const { data } = await client.query({
            query: GenerateReadyPlanVersionNotesDocument as TypedDocumentNode<
              GenerateReadyPlanVersionNotesQuery,
              GenerateReadyPlanVersionNotesQueryVariables
            >,
            variables: { readyPlanId: po.id },
            fetchPolicy: "no-cache",
          });
          return [po.id, data?.generateReadyPlanVersionNotes.notes] as const;
        }),
      );
      setGenerating(false);
      // An empty string means no changes were detected worth summarizing
      const generated = results.flatMap((r) => (r.status === "fulfilled" && r.value[1] ? [r.value] : []));
      if (results.some((r) => r.status === "rejected")) {
        triggerNotice({ icon: "error", message: "Could not generate some version notes. Please write them manually." });
      } else if (generated.isEmpty) {
        triggerNotice({
          icon: "warning",
          message: "No changes were detected to summarize. You can write the notes manually.",
        });
      }
      if (generated.nonEmpty) {
        setVersionNotes(Object.fromEntries(generated));
        setHasGenerated(true);
      }
    }
    void generateNotes();
  }, [client, productOfferings, triggerNotice]);

  async function publish() {
    onPublish();
    const { data } = await save({
      variables: {
        input: productOfferings.map((po) => ({
          id: (po.aggregateDraft ?? fail(`${po.name} has no draft to publish`)).id,
          status: AggregateVersionStatus.Active,
          versionNotes: versionNotes[po.id],
        })),
      },
    });
    // Each version is published on its own, so ones that fail validation don't block the rest
    const { versions = [], failures = [] } = data?.saveReadyPlanAggregateVersions ?? {};
    const total = count(productOfferings, "Product Offering");
    triggerNotice({
      message: `${failures.isEmpty ? total : `${versions.length} of ${total}`} ${productOfferings.length === 1 ? "was" : "were"} published.`,
    });
    // One error per failed offering
    failures.forEach(({ version, message }) => {
      const po = productOfferings.find((po) => po.aggregateDraft?.id === version.id);
      triggerNotice({ icon: "error", persistent: true, message: `${po?.name}: ${message}` });
    });
  }

  function clearNotes() {
    setVersionNotes({});
    setHasGenerated(false);
  }

  return (
    <ConfirmationModal
      title={label}
      label={label}
      onConfirmAction={publish}
      banner={
        hasGenerated && !generating ? (
          <AiSlimBanner
            title="AI-drafted from detected changes. Edit as needed."
            action={{ label: "Clear", onClick: clearNotes }}
          />
        ) : undefined
      }
      confirmationMessage={
        <div css={Css.df.fdc.gap3.$}>
          <p css={Css.sm.$}>
            These version notes will be visible in History and can help teams understand what changed in each Product
            Offering.
          </p>
          <div css={Css.df.aic.gap1.$}>
            <span css={Css.lg.$}>Version Notes</span>
            {generating && <AiLoader inc={2} label="Generating version notes" />}
          </div>
          {productOfferings.map((po) => (
            // On narrow screens the notes move under the offering so its name and versions stay readable
            <div key={po.id} css={Css.df.aic.gap3.ifSm.fdc.ais.$} {...tid.productOffering}>
              <div css={Css.df.aic.gap3.fg1.mw0.$}>
                <img
                  src={po.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG}
                  alt=""
                  css={Css.wPx(108).hPx(108).fs0.br8.objectCover.ifSm.wPx(72).hPx(72).$}
                />
                <div css={Css.df.fdc.mw0.$}>
                  <span css={Css.smSb.truncate.$}>{po.name}</span>
                  <span css={Css.sm.gray700.$} {...tid.versions}>
                    {po.aggregateActive && `Current ${po.aggregateActive.displayVersion} → `}Publish{" "}
                    {po.aggregateLatest.displayVersion}
                  </span>
                </div>
              </div>
              <div css={Css.wPx(350).fs0.ifSm.w100.$}>
                <RichTextField
                  value={versionNotes[po.id]}
                  onChange={(notes) => setVersionNotes((prev) => ({ ...prev, [po.id]: notes }))}
                  placeholder={generating ? "Reviewing the draft and summarizing changes…" : undefined}
                  {...tid.versionNotes}
                />
              </div>
            </div>
          ))}
        </div>
      }
    />
  );
}
